import type { ReactNode } from 'react';
import type { TileOf } from '@casa/model';
import { drawElements } from '../../elements/layout_elements.ts';
import { CanvasView } from '../../view/canvas_view.tsx';
import { useLegacyIcon } from '../../view/icons.ts';
import { TILE_FONT } from '../../view/theme.ts';
import { useTileTextColor } from '../../view/tile_style.ts';
import { useTileEnvironment } from '../../view/tile_environment.ts';
import { WHITE, airQualityColumns, airQualityTileElements } from './air_quality_tile.ts';

function field(value: unknown, key: string) : number | null {
    const field = typeof value === 'object' && value !== null ? (value as Record<string, unknown>)[key] : undefined;

    return typeof field === 'number' && Number.isFinite(field) ? field : null;
}

/** The legacy `airquality` tile, fed by the AirGradient driver's reading. */
export function AirQualityTileView({ tile }: { tile: TileOf<'airquality'> }) : ReactNode {
    const environment = useTileEnvironment();
    const textColor = useTileTextColor(WHITE);
    const reading = environment.useReadings(tile, tile.options.source)?.at(-1)?.value;
    const columns = airQualityColumns(
        {
            co2: field(reading, 'co2'),
            pm25: field(reading, 'pm25'),
            tvocPpb: field(reading, 'tvoc_ppb'),
            co2Trend: field(reading, 'co2_trend'),
            pm25Trend: field(reading, 'pm25_trend'),
            tvocPpbTrend: field(reading, 'tvoc_ppb_trend'),
        },
        environment.locale,
    );
    const icons = { up: useLegacyIcon('arrow_trend_up'), down: useLegacyIcon('arrow_trend_down'), flat: useLegacyIcon('equals') };

    return (
        <div style={{ position: 'absolute', inset: 0 }}>
            <CanvasView
                deps={[JSON.stringify(columns), icons.up, icons.down, icons.flat, textColor]}
                draw={(ctx, width, height) => drawElements(ctx, { left: 0, top: 0, width, height }, airQualityTileElements(columns, icons, TILE_FONT, textColor))}
            />
        </div>
    );
}
